import { random } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TextButtonExpandable } from '../../../components/UI/Buttons';
import { Asset } from '../../../queries/ats/titles/types/asset';
import { Cast } from '../../../queries/ats/titles/types/shared';
import { Source } from '../../../queries/ats/titles/types/sources';
import { classes } from '../../../utils/commonUtils';
import { DetailsSpacing } from '../details-spacing/details-spacing';
import css from './details-metadata.module.scss';
import { Metadata } from './utils';

interface DetailsMetadataProps {
  asset: Asset;
  source: Source;
}

export function DetailsMetadata({ asset, source }: DetailsMetadataProps) {
  const metadata = useMemo(() => Metadata.build(asset, source), [asset, source]);
  const details = metadata.details.filter((detail) => detail.value !== '');

  const { t } = useTranslation();

  return (
    <DetailsSpacing type="margin">
      <div className={css.container}>
        <People heading={t('details:actors')} people={metadata.actors} />
        <People heading={t('details:directors')} people={metadata.directors.concat(metadata.producers)} />

        <section className={css.data}>
          <h3 className={css.heading}>{t('details:details')}</h3>
          <dl className={css.details}>
            {details.map((detail) => (
              <span className={css.group} key={detail.label}>
                <dt className={css.label}>{detail.label}:</dt>
                <dd className={css.value}>{detail.value}</dd>
              </span>
            ))}
          </dl>
        </section>
      </div>
    </DetailsSpacing>
  );
}

function DetailsMetadataSkeleton() {
  return (
    <DetailsSpacing type="margin">
      <div className={css.container}>
        <People.Skeleton />
        <People.Skeleton />

        <section className={css.data}>
          <div className={classes(css.heading, css.skeleton)} />
          <div className={css.details}>
            <div className={css.group}>
              <div
                className={classes(css.label, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
              <div
                className={classes(css.value, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
            </div>
            <div className={css.group}>
              <div
                className={classes(css.label, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
              <div
                className={classes(css.value, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
            </div>
            <div className={css.group}>
              <div
                className={classes(css.label, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
              <div
                className={classes(css.value, css.skeleton)}
                style={{ '--random': random(40, 100) } as React.CSSProperties}
              />
            </div>
          </div>
        </section>
      </div>
    </DetailsSpacing>
  );
}

DetailsMetadata.Skeleton = DetailsMetadataSkeleton;

interface PeopleProps {
  heading: string;
  people: Cast[];
  expandLimit?: number;
}

function People({ heading, people, expandLimit = 3 }: PeopleProps) {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const canExpand = people.length > expandLimit;
  const count = expanded ? people.length : expandLimit;

  if (people.length === 0) {
    return null;
  }

  return (
    <section className={css.data}>
      <h3 className={css.heading}>{heading}</h3>
      <ul className={css.people}>
        {people.slice(0, count).map((director) => (
          <Person key={director.name + director.role} person={director} />
        ))}

        {canExpand && (
          <TextButtonExpandable className={css.expand} expanded={expanded} onClick={() => setExpanded((x) => !x)}>
            {expanded ? t('show less') : t('show more')}
          </TextButtonExpandable>
        )}
      </ul>
    </section>
  );
}

function PeopleSkeleton() {
  return (
    <section className={css.data}>
      <div
        className={classes(css.heading, css.skeleton)}
        style={{ '--random': random(100, 200) } as React.CSSProperties}
      />
      <ul className={css.people}>
        {Array.from({ length: random(1, 5) }).map((_, idx) => (
          <li key={idx}>
            <div className={classes(css.person, css.skeleton)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

People.Skeleton = PeopleSkeleton;

function Person({ person }: { person: Cast }) {
  return (
    <li>
      <Link className={css.person} to={`/search?q=${person.name}`}>
        {person.name}
      </Link>
    </li>
  );
}
